/**
 * Tags List View
 *
 * Tags list with learning goal filter, language selector, and inline editing.
 * Tags are loaded from initial data with all localizations.
 *
 * Story 10.5 - Task 4: Admin Frontend UI
 * Updated Story 15.7.2: Tags now seeded via migration, removed create/update/delete
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { DictionariesNavigation } from '../components/DictionariesNavigation';
import { useInitialData, type Tag } from '../../../contexts/InitialDataContext';
import { useUpdateTagLocalization } from '../services/tagsApi';

/**
 * Get localized name for a tag
 * Falls back to English if requested language not available
 */
function getTagLocalizedName(tag: Tag, languageCode: string): string {
  const localization = tag.localizations.find((loc) => loc.language_code === languageCode);
  if (localization) return localization.localized_name;

  // Fallback to English
  const englishLoc = tag.localizations.find((loc) => loc.language_code === 'en');
  if (englishLoc) return englishLoc.localized_name;

  // Fallback to first available
  if (tag.localizations.length > 0) return tag.localizations[0].localized_name;

  return `Tag ${tag.id}`;
}

/**
 * Check if tag has localization for given language
 */
function hasLocalization(tag: Tag, languageCode: string): boolean {
  return tag.localizations.some((loc) => loc.language_code === languageCode);
}

/**
 * Inline editable cell component
 */
function EditableCell({
  tagId,
  languageCode,
  value,
  canEdit,
  onSave,
  isSaving,
}: {
  tagId: number;
  languageCode: string;
  value: string;
  canEdit: boolean;
  onSave: (tagId: number, languageCode: string, newValue: string) => void;
  isSaving: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Reset edit value when tag value changes (after save)
  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleDoubleClick = () => {
    if (canEdit && !isSaving) {
      setIsEditing(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(value);
      setIsEditing(false);
    }
  };

  const handleSave = () => {
    const trimmedValue = editValue.trim();
    if (trimmedValue && trimmedValue !== value) {
      onSave(tagId, languageCode, trimmedValue);
    }
    setIsEditing(false);
  };

  const handleBlur = () => {
    handleSave();
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className="w-full px-2 py-1 text-sm border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        data-testid={`edit-input-${tagId}`}
      />
    );
  }

  return (
    <div
      onDoubleClick={handleDoubleClick}
      className={`text-sm font-medium ${
        canEdit
          ? 'text-gray-900 cursor-pointer hover:bg-gray-50 px-2 py-1 -mx-2 -my-1 rounded'
          : 'text-gray-500 italic'
      } ${isSaving ? 'opacity-50' : ''}`}
      title={canEdit ? 'Double-click to edit' : 'No localization for this language'}
      data-testid={`tag-name-${tagId}`}
    >
      {value}
      {!canEdit && ' (fallback)'}
    </div>
  );
}

export function TagsListView() {
  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedLearningGoalId, setSelectedLearningGoalId] = useState<number | null>(null);
  const [displayLanguage, setDisplayLanguage] = useState<string>('en');
  const [savingTagId, setSavingTagId] = useState<number | null>(null);

  const { state: initialDataState } = useInitialData();
  const updateTagLocalization = useUpdateTagLocalization();

  const learningGoals = initialDataState.data?.learningGoals ?? [];
  const languages = initialDataState.data?.languages ?? [];

  // Filter and sort tags
  const filteredTags = useMemo(() => {
    const tags = initialDataState.data?.tags ?? [];
    let result = tags;

    // Filter by learning goal
    if (selectedLearningGoalId !== null) {
      result = result.filter((tag) => tag.learning_goal_ids.includes(selectedLearningGoalId));
    }

    // Filter by search (on localized name)
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter((tag) =>
        getTagLocalizedName(tag, displayLanguage).toLowerCase().includes(searchLower)
      );
    }

    // Sort by localized name
    result = [...result].sort((a, b) => {
      const nameA = getTagLocalizedName(a, displayLanguage).toLowerCase();
      const nameB = getTagLocalizedName(b, displayLanguage).toLowerCase();
      const comparison = nameA.localeCompare(nameB);
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [initialDataState.data?.tags, selectedLearningGoalId, search, displayLanguage, sortOrder]);

  const handleSort = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const handleSave = (tagId: number, languageCode: string, newValue: string) => {
    setSavingTagId(tagId);
    updateTagLocalization.mutate(
      { tagId, languageCode, localizedName: newValue },
      {
        onSettled: () => {
          setSavingTagId(null);
        },
      }
    );
  };

  if (initialDataState.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (initialDataState.error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error loading tags</p>
          <p className="text-gray-600">{initialDataState.error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <DictionariesNavigation />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Tags</h1>
          <p className="text-gray-600 mt-1">
            Double-click a tag name to edit. Select a language to view and edit localizations.
          </p>
        </div>
      </div>

      {/* Filters Row */}
      <div className="mb-6 flex gap-4">
        {/* Search Bar */}
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search tags by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="search-input"
          />
        </div>

        {/* Learning Goal Filter */}
        <div className="w-64">
          <select
            value={selectedLearningGoalId ?? ''}
            onChange={(e) =>
              setSelectedLearningGoalId(e.target.value ? Number(e.target.value) : null)
            }
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            data-testid="learning-goal-filter"
          >
            <option value="">All Learning Goals</option>
            {learningGoals.map((goal) => (
              <option key={goal.id} value={goal.id}>
                {goal.icon_emoji ? `${goal.icon_emoji} ` : ''}
                {goal.name}
              </option>
            ))}
          </select>
        </div>

        {/* Display Language Selector */}
        <div className="w-48">
          <select
            value={displayLanguage}
            onChange={(e) => setDisplayLanguage(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            data-testid="display-language-selector"
          >
            {languages
              .filter((lang) => lang.code !== 'zxx')
              .map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
          </select>
        </div>
      </div>

      {/* Tags Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200" data-testid="tags-table">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={handleSort}
                data-testid="sort-name"
              >
                Name {sortOrder === 'asc' ? '↑' : '↓'}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Learning Goals
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Available Localizations
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {/* Tag Rows */}
            {filteredTags.map((tag) => (
              <tr key={tag.id} data-testid={`tag-row-${tag.id}`}>
                <td className="px-6 py-4">
                  <EditableCell
                    tagId={tag.id}
                    languageCode={displayLanguage}
                    value={getTagLocalizedName(tag, displayLanguage)}
                    canEdit={hasLocalization(tag, displayLanguage)}
                    onSave={handleSave}
                    isSaving={savingTagId === tag.id}
                  />
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {tag.learning_goal_ids.map((goalId) => {
                      const goal = learningGoals.find((g) => g.id === goalId);
                      if (!goal) return null;
                      return (
                        <span
                          key={goalId}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                          title={goal.name}
                        >
                          {goal.icon_emoji ? `${goal.icon_emoji} ` : ''}
                          {goal.name}
                        </span>
                      );
                    })}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {tag.localizations
                      .sort((a, b) => a.language_code.localeCompare(b.language_code))
                      .map((loc) => (
                        <span
                          key={loc.language_code}
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            loc.language_code === displayLanguage
                              ? 'bg-blue-100 text-blue-800 ring-1 ring-blue-500'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                          title={loc.localized_name}
                        >
                          {loc.language_code.toUpperCase()}
                        </span>
                      ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {filteredTags.length === 0 && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center" data-testid="empty-state">
          <p className="text-gray-600 text-lg">
            {selectedLearningGoalId
              ? 'No tags found for the selected learning goal.'
              : 'No tags found matching your search.'}
          </p>
        </div>
      )}
    </div>
  );
}
